import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import ConversationList from './components/ConversationList';
import MessageThread from './components/MessageThread';
import MessageInput from './components/MessageInput';

const FamilyMessagingInterface = () => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');

  // Load family members from localStorage
  useEffect(() => {
    const savedMembers = localStorage?.getItem('familyMembers');
    if (savedMembers) {
      setFamilyMembers(JSON.parse(savedMembers));
    } else {
      // Default members if none exist
      const defaultMembers = [
        { id: 1, name: 'Sarah', role: 'Mom', color: '#EF4444', initials: 'SM' },
        { id: 2, name: 'Michael', role: 'Dad', color: '#3B82F6', initials: 'MD' },
        { id: 3, name: 'Emma', role: 'Daughter', color: '#EC4899', initials: 'ED' },
        { id: 4, name: 'Lucas', role: 'Son', color: '#10B981', initials: 'LS' }
      ];
      setFamilyMembers(defaultMembers);
    }
  }, []);

  // Load conversations from localStorage
  useEffect(() => {
    const savedConversations = localStorage?.getItem('familyConversations');
    if (savedConversations) {
      setConversations(JSON.parse(savedConversations));
    } else {
      // Default conversations
      const defaultConversations = [
        {
          id: 1,
          title: 'Weekend Plans',
          lastMessage: 'Should we go to the park on Saturday?',
          lastMessageTime: new Date()?.toISOString(),
          unreadCount: 2,
          messages: [
            {
              id: 1,
              senderId: 1,
              content: 'What are we doing this weekend?',
              timestamp: new Date(Date.now() - 3600000)?.toISOString(),
              read: true
            },
            {
              id: 2,
              senderId: 2,
              content: 'Should we go to the park on Saturday?',
              timestamp: new Date()?.toISOString(),
              read: false
            }
          ]
        },
        {
          id: 3,
          title: 'School Events',
          lastMessage: 'Emma has a science fair next Friday',
          lastMessageTime: new Date(Date.now() - 86400000)?.toISOString(),
          unreadCount: 1,
          messages: [
            {
              id: 1,
              senderId: 3,
              content: 'Emma has a science fair next Friday',
              timestamp: new Date(Date.now() - 86400000)?.toISOString(),
              read: false
            }
          ]
        }
      ];
      setConversations(defaultConversations);
      localStorage?.setItem('familyConversations', JSON.stringify(defaultConversations));
    }
  }, []);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    if (conversations?.length > 0) {
      localStorage?.setItem('familyConversations', JSON.stringify(conversations));
    }
  }, [conversations]);

  const handleSendMessage = (content, mentions = []) => {
    if (!activeConversationId || !content?.trim()) return;

    const newMessage = {
      id: Date.now(),
      senderId: 1, // Default to first member (would be current user in real app)
      content: content?.trim(),
      timestamp: new Date()?.toISOString(),
      read: false,
      mentions
    };

    setConversations(prev => prev?.map(conv => {
      if (conv?.id === activeConversationId) {
        return {
          ...conv,
          messages: [...(conv?.messages || []), newMessage],
          lastMessage: content?.trim(),
          lastMessageTime: newMessage?.timestamp
        };
      }
      return conv;
    }));
  };

  const handleUpdateGroceryItems = (updatedItems) => {
    setConversations(prev => prev?.map(conv => {
      if (conv?.id === activeConversationId && conv?.isGroceryList) {
        const checkedCount = updatedItems?.filter(item => item?.checked)?.length || 0;
        const totalCount = updatedItems?.length || 0;
        return {
          ...conv,
          groceryItems: updatedItems,
          lastMessage: `${checkedCount}/${totalCount} items completed`,
          lastMessageTime: new Date()?.toISOString()
        };
      }
      return conv;
    }));
  };

  const handleCreateThread = () => {
    if (!newThreadTitle?.trim()) return;

    const newConversation = {
      id: Date.now(),
      title: newThreadTitle?.trim(),
      lastMessage: '',
      lastMessageTime: new Date()?.toISOString(),
      unreadCount: 0,
      messages: []
    };

    setConversations(prev => [newConversation, ...prev]);
    setActiveConversationId(newConversation?.id);
    setNewThreadTitle('');
    setShowNewThreadModal(false);
  };

  const handleMarkAsRead = (conversationId) => {
    setConversations(prev => prev?.map(conv => {
      if (conv?.id === conversationId) {
        return {
          ...conv,
          unreadCount: 0,
          messages: conv?.messages?.map(msg => ({ ...msg, read: true }))
        };
      }
      return conv;
    }));
  };

  const activeConversation = conversations?.find(c => c?.id === activeConversationId);

  return (
    <>
      <Helmet>
        <title>Family Messages - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <TabNavigation />
        
        <div className="container mx-auto px-4 py-6">
          <div className="mb-6">
            <h1 className="text-h1 font-heading text-foreground mb-2">
              Family Messages
            </h1>
            <p className="text-muted-foreground">
              Coordinate tasks and share updates with threaded conversations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-280px)]">
            {/* Conversation List Panel */}
            <div className="lg:col-span-1 bg-card border border-border rounded-lg overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground">Conversations</h2>
                <Button
                  size="sm"
                  onClick={() => setShowNewThreadModal(true)}
                  iconName="plus"
                  title="New conversation"
                >
                  New
                </Button>
              </div>
              
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                onSelectConversation={(id) => {
                  setActiveConversationId(id);
                  handleMarkAsRead(id);
                }}
                familyMembers={familyMembers}
              />
            </div>

            {/* Message Thread Panel */}
            <div className="lg:col-span-2 bg-card border border-border rounded-lg overflow-hidden flex flex-col">
              {activeConversation ? (
                <>
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">
                          {activeConversation?.title}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {activeConversation?.messages?.length || 0} messages
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setActiveConversationId(null)}
                        className="lg:hidden"
                      >
                        <Icon name="x" size={20} />
                      </Button>
                    </div>
                  </div>

                  <MessageThread
                    messages={activeConversation?.messages || []}
                    familyMembers={familyMembers}
                  />

                  <MessageInput
                    onSendMessage={handleSendMessage}
                    familyMembers={familyMembers}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-center p-8">
                  <div>
                    <Icon name="message-square" size={48} className="mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-muted-foreground">
                      Choose a conversation from the list or create a new one
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* New Thread Modal */}
        {showNewThreadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                New Conversation
              </h3>
              <input
                type="text"
                value={newThreadTitle}
                onChange={(e) => setNewThreadTitle(e?.target?.value)}
                placeholder="Conversation title..."
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground mb-4"
                autoFocus
                onKeyDown={(e) => {
                  if (e?.key === 'Enter') {
                    handleCreateThread();
                  }
                }}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowNewThreadModal(false);
                    setNewThreadTitle('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateThread}
                  disabled={!newThreadTitle?.trim()}
                >
                  Create
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FamilyMessagingInterface;